import type { Messages } from '../i18n';
import { ApiError } from './api';

/**
 * The sentence a person reads for a failed request (ADR 0005).
 *
 * The API names a refusal by a code in the problem's type, and the words are
 * the panel's: the code's own sentence, or, for a code this build has no words
 * for or a request no screen was meant to send, the sentence for its status.
 * The problem's detail is English, for integrators, and is never shown.
 */
export function problemText(t: Messages, error: unknown): string {
    if (!(error instanceof ApiError)) return t.common.genericError;
    return codedText(t, error) ?? t.errors.status[error.status as keyof typeof t.errors.status] ?? t.common.genericError;
}

/**
 * A refusal's field errors, keyed by field: the problem's own sentence where it
 * has one, and a plain "check this field" otherwise — never the API's schema
 * message.
 */
export function problemFields(t: Messages, error: unknown): Record<string, string> {
    if (!(error instanceof ApiError)) return {};
    const sentence = codedText(t, error) ?? t.errors.field;
    return Object.fromEntries(Object.keys(error.fieldErrors()).map((field) => [field, sentence]));
}

/**
 * The refusal's own code, for the rare screen that answers one with more than
 * words — the link that fixes it. The sentence still comes from problemText;
 * this only says which refusal was met.
 */
export function problemCode(error: unknown): string | undefined {
    return error instanceof ApiError ? error.code : undefined;
}

function codedText(t: Messages, error: ApiError): string | undefined {
    const code = error.code;
    const entry: string | ((quote: string) => string) | undefined = code ? t.errors.problems[code as keyof typeof t.errors.problems] : undefined;
    // An entry that takes the quote is a refusal in a carrier's or a provider's
    // own words, the one kind of detail a screen shows (docs/15 §6).
    if (typeof entry === 'function') return error.problem.detail ? entry(error.problem.detail) : undefined;
    return entry;
}
